import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaUpload, 
  FaTrash, 
  FaImage, 
  FaFile, 
  FaVideo,
  FaSearch,
  FaTimes,
  FaDownload
} from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminMediaLibrary = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await apiService.getMedia();
      setMedia(response.data || []);
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Failed to fetch media');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files[]', file);
      });

      await apiService.uploadMedia(formData);
      toast.success(`${selectedFiles.length} file(s) uploaded successfully`);
      setSelectedFiles([]);
      fetchMedia();
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        await apiService.deleteMedia(id);
        toast.success('File deleted successfully');
        fetchMedia();
      } catch (error) {
        toast.error('Failed to delete file');
      }
    }
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return <FaImage className="text-blue-400" />;
    if (type?.startsWith('video/')) return <FaVideo className="text-purple-400" />;
    return <FaFile className="text-gray-400" />;
  };

  const filteredMedia = media.filter(item =>
    item.original_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.filename?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Media Library</h1>
          <p className="text-gray-400 mt-1">Manage your media files</p>
        </div>
      </div>

      {/* Upload Section */}
      <div className="glass rounded-2xl p-6 mb-8">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1">
            <label className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-all duration-300">
              <div className="text-center">
                <FaUpload className="text-2xl text-gray-400 mx-auto mb-2" />
                <span className="text-gray-400 text-sm">Drop files here or click to browse</span>
                <span className="block text-xs text-gray-500 mt-1">Images, videos, PDFs (Max 10MB each)</span>
              </div>
              <input
                type="file"
                className="hidden"
                multiple
                onChange={handleFileSelect}
                accept="image/*,video/*,.pdf,.doc,.docx"
              />
            </label>
          </div>
          {selectedFiles.length > 0 && (
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-400">{selectedFiles.length} file(s) selected</span>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                onClick={() => setSelectedFiles([])}
                className="p-2 rounded-lg hover:bg-white/10 transition-all duration-300 text-gray-400"
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search media..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Media Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {filteredMedia.map((item) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass rounded-xl overflow-hidden hover:bg-white/10 transition-all duration-300 group"
          >
            <div className="relative aspect-square">
              {item.file_type?.startsWith('image/') ? (
                <img 
                  src={item.file_path} 
                  alt={item.alt_text || item.original_name}
                  className="w-full h-full object-cover"
                />
              ) : item.file_type?.startsWith('video/') ? (
                <video 
                  src={item.file_path}
                  className="w-full h-full object-cover"
                  controls
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-white/5">
                  {getFileIcon(item.file_type)}
                </div>
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
                <button
                  onClick={() => handleDelete(item.id)}
                  className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-all duration-300 text-red-400"
                >
                  <FaTrash size={14} />
                </button>
                <a
                  href={item.file_path}
                  download
                  className="p-2 rounded-lg bg-primary-500/20 hover:bg-primary-500/30 transition-all duration-300 text-primary-400"
                >
                  <FaDownload size={14} />
                </a>
              </div>
            </div>
            <div className="p-2">
              <p className="text-xs text-gray-400 truncate" title={item.original_name}>
                {item.original_name}
              </p>
              <p className="text-xs text-gray-500">
                {(item.file_size / 1024).toFixed(1)} KB
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredMedia.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400">No media files found. Upload some files!</p>
        </div>
      )}
    </div>
  );
};

export default AdminMediaLibrary;